import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/app/global-components/input";
import { useNavigate } from "react-router-dom";
import regionsData from "@/app/features/company/data/regions_lite.json";
//import citiesData from "@/app/features/company/data/cities_lite.json";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Pagination, useMediaQuery } from "@mui/material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import useOffer from "@/hooks/useTender";

interface Tender {
  id: string;
  title: string;
  value: number;
  region: string;
  city: string;
  type: string;
  status: string;
  endDate: string;
  deliverDate: string;
  categories: string[];
  TenderNr: number;
}

/*interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}
*/
interface Region {
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

const TenderTable: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTenders, setFilteredTenders] = useState<Tender[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [regions, setRegions] = useState<Option[]>([]);
  //const [cities, setCities] = useState<Option[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const isMobile = useMediaQuery("(max-width:600px)");

  const navigate = useNavigate();
  const { getMyOffers } = useOffer();

  useEffect(() => {
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );
  }, []);

  /*useEffect(() => {
    setCities(
      citiesData.map((city: City) => ({
        value: city.city_id.toString(),
        label: city.name_ar,
      }))
    );
  }, []);
  */
  /*
  const getCity = (value: string) => {
    const city = cities.find((item) => parseInt(item.value) === parseInt(value));
    return city ? city.label : "";
  };
*/
  const getRegion = (value: string) => {
    const region = regions.find(
      (item) => parseInt(item.value) === parseInt(value)
    );
    return region ? region.label : "";
  };
  useEffect(() => {
    const fetchTenders = async () => {
      try {
        const Offers = await getMyOffers();
        if (Offers) {
          setTenders(Offers.data);
        } else {
          setTenders([]);
        }
      } catch (error) {
        console.error("Error fetching tenders:", error);
      }
    };
    fetchTenders();
  }, []);

  useMemo(() => {
    let filtered = tenders.filter((tender) =>
      tender.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortField) {
      filtered.sort((a, b) => {
        const aField = a[sortField as keyof Tender];
        const bField = b[sortField as keyof Tender];
        if (aField === undefined || bField === undefined) return 0;
        if (aField < bField) return sortDirection === "asc" ? -1 : 1;
        if (aField > bField) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredTenders(filtered);
  }, [searchTerm, tenders, sortField, sortDirection]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTenders = filteredTenders.slice(startIndex, endIndex);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (field: string) => {
    setSortDirection(
      sortField === field && sortDirection === "asc" ? "desc" : "asc"
    );
    setSortField(field);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <div className="p-6">
        <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
          <Input
            type="text"
            placeholder={t("actions.searching")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
          />
        </div>

        {isMobile ? (
          // Mobile View
          <div className="flex flex-col gap-4">
            {paginatedTenders.map((tender) => (
              <div
                key={tender.id}
                className="p-4 border rounded-lg shadow-sm hover:shadow-md transition duration-200 bg-white"
                onClick={() => navigate(`${routes.vuetender}/${tender.id}`)}
              >
                <h3 className="font-semibold text-lg">{tender.TenderNr}</h3>
                <h3 className="font-semibold text-lg">{tender.title}</h3>
                <p className="text-sm text-gray-500">
                  {tender.value} {t("currency")}
                </p>
                <div className="mt-2 text-sm text-gray-500">
                  <p>
                    {t("company.fields.region")}: {getRegion(tender.region)}
                  </p>
                  <p>
                    {t("orders.fields.status")}: {tender.status}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${routes.vuetender}/${tender.id}`);
                  }}
                  className="p-2 rounded-full bg-black text-white hover:bg-gray-800 mt-4"
                >
                  <VisibilityIcon fontSize="small" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          // Desktop View
          <table
            className="w-full bg-white rounded-lg overflow-hidden"
            style={{ minWidth: 1200 }}
          >
            <thead className="bg-gray-200">
              <tr>
                <th
                  className="p-4 cursor-pointer"
                  onClick={() => handleSort("TenderNr")}
                >
                  {t("orders.number")}{" "}
                  {sortField === "TenderNr" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="p-4 cursor-pointer"
                  onClick={() => handleSort("title")}
                >
                  {t("company.fields.address")}{" "}
                  {sortField === "title" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="p-4 cursor-pointer"
                  onClick={() => handleSort("tender.fields.value")}
                >
                  {t("tender.value")}{" "}
                  {sortField === "value" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th className="p-4">{t("company.fields.region")}</th>
                <th className="p-4">{t("orders.fields.status")}</th>
                <th className="p-4">ال{t("general.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTenders.map((tender) => (
                <tr
                  key={tender.id}
                  className="border-b hover:bg-gray-100 cursor-pointer"
                  onClick={() => navigate(`${routes.vuetender}/${tender.id}`)}
                >
                  <td className="p-4">{tender.TenderNr}</td>
                  <td className="p-4">{tender.title}</td>
                  <td className="p-4">
                    {tender.value} {t("currency")}
                  </td>
                  <td className="p-4">{getRegion(tender.region)}</td>
                  <td className="p-4">{tender.status}</td>
                  <td className="p-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`${routes.vuetender}/${tender.id}`);
                      }}
                      className="p-2 rounded-full bg-black text-white flex items-center justify-center hover:bg-gray-800"
                    >
                      <VisibilityIcon fontSize="small" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={Math.ceil(filteredTenders.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  backgroundColor: "#644980",
                  color: "#fff",
                },
                "&:hover": {
                  backgroundColor: "#b392c9",
                  color: "#fff",
                },
              },
            }}
          />
        </Box>
      </div>
    </div>
  );
};

export default TenderTable;
